 "use client"

import Image from "next/image"
import { Button } from "@/components/ui/button"
import { ArrowLeft, HelpCircle } from "lucide-react"
import { useRef, useCallback, useState } from "react"
import { logger } from "@/lib/logger"
import BugReportModal, { type BugReportContext } from "@/app/components/bug-report-modal"
import HelpOverlay from "@/app/components/functions/help/help-overlay"

interface LogoHeaderProps {
  onLogoClick?: () => void
  onBackClick?: () => void
  showBackButton?: boolean
  title?: string
  className?: string
  /** Optional context for bug reports (e.g. current screen name, file type). */
  bugReportContext?: BugReportContext
}

const LONG_PRESS_DURATION = 800; // ms

/**
 * Help, in the top bar rather than in the page.
 *
 * It sat under the upload cards on the start page to begin with, centred while
 * the tips accordion beside it was flush left - two bold links of similar weight,
 * doing similar-sounding jobs, aligned differently. Up here it is where people
 * look for help, it stops competing with the two things the start page is
 * actually for, and it matches the reading screen, which carries the same icon in
 * its own toolbar.
 *
 * In this shared header it therefore appears on every screen that uses it - the
 * camera, the previews, processing - which is a feature rather than a cost: those
 * are the screens a reader is most likely to be stuck on. The reading screen has
 * its own header and its own copy.
 */
function HelpButton({ onClick }: { onClick: () => void }) {
  return (
    <Button
      variant="ghost"
      size="icon"
      onClick={onClick}
      className="text-white hover:bg-white/10 hover:text-white"
      aria-label="Pomoc"
    >
      <HelpCircle className="h-5 w-5" />
    </Button>
  )
}

export default function LogoHeader({ 
  onLogoClick, 
  onBackClick, 
  showBackButton = false, 
  title,
  className = "",
  bugReportContext,
}: LogoHeaderProps) {
  const pressTimer = useRef<NodeJS.Timeout | null>(null);
  const longPressTriggered = useRef(false);
  const [bugModalOpen, setBugModalOpen] = useState(false);
  const [helpOpen, setHelpOpen] = useState(false);

  const openBugReport = useCallback(() => {
    setBugModalOpen(true);
    logger.info("Bug report modal opened from header");
  }, []);

  const handlePressStart = useCallback(() => {
    longPressTriggered.current = false;
    pressTimer.current = setTimeout(() => {
      longPressTriggered.current = true;
      openBugReport();
    }, LONG_PRESS_DURATION);
  }, [openBugReport]);

  const handlePressEnd = useCallback(() => {
    if (pressTimer.current) {
      clearTimeout(pressTimer.current);
      pressTimer.current = null;
    }
    // Only trigger click if long press wasn't triggered
    if (!longPressTriggered.current && onLogoClick) {
      onLogoClick();
    }
  }, [onLogoClick]);

  const handleContextMenu = useCallback((e: React.MouseEvent) => {
    // Prevent context menu on long press (mobile)
    if (longPressTriggered.current) {
      e.preventDefault();
    }
  }, []);

  return (
    <header className={`md:border-b px-4 bg-[rgba(43,4,72,1)] pb-0 md:pb-0 ${className}`}>
      <div className="max-w-6xl mx-auto">
        {/* Desktop: logo on the left, back button on the right */}
        <div className="hidden md:flex items-center justify-between py-4">
          <button 
            onMouseDown={handlePressStart}
            onMouseUp={handlePressEnd}
            onMouseLeave={() => {
              if (pressTimer.current) {
                clearTimeout(pressTimer.current);
                pressTimer.current = null;
              }
            }}
            onTouchStart={handlePressStart}
            onTouchEnd={handlePressEnd}
            onContextMenu={handleContextMenu}
            className="hover:opacity-80 transition-opacity cursor-pointer select-none"
            aria-label="Przejdź do strony głównej"
          >
            <Image
              src="/images/design-mode/logo.png"
              alt="Dyslexa"
              width={150}
              height={40}
              className="h-[40px] w-auto pointer-events-none"
              draggable={false}
            />
          </button>
          <div className="flex items-center gap-2">
            {showBackButton && onBackClick && (
              <Button
                variant="ghost"
                size="sm"
                onClick={onBackClick}
                className="flex items-center space-x-2 text-white hover:bg_white/10 hover:text-white"
              >
                <ArrowLeft className="h-4 w-4" />
                <span>Wstecz</span>
              </Button>
            )}
            <HelpButton onClick={() => setHelpOpen(true)} />
          </div>
        </div>

        {/* Mobile: back button on the left, logo/title in the middle */}
        <div className="md:hidden flex flex-col pt-3 pb-2">
          {/* Top row: three grid columns - back button, logo/title, empty right column.
              The two 1fr columns are a matched pair. They centre the middle cell on the row
              itself instead of between whatever the sides happen to hold, so the right column
              can sit empty (it used to carry the bug-report button) without the logo sliding
              off centre. Fill both columns and the middle stays put; fill only one and it
              drifts. On a narrow screen a wide back button can still push its column past
              its share - it grows rather than overlapping the title. */}
          <div className="grid grid-cols-[1fr_auto_1fr] items-center">
            <div className="flex justify-start">
              {showBackButton && onBackClick && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={onBackClick}
                  className="flex items-center gap-1 text_white hover:bg_white/10 hover:text_white px-2 -ml-2"
                >
                  <ArrowLeft className="h-4 w-4" />
                  <span className="text-sm">Wstecz</span>
                </Button>
              )}
            </div>
            
            {/* Show title if present, otherwise show logo */}
            {title ? (
              <h1 className="text-xl font-bold text-white flex items_center gap-1.5">
                {title}
                <Image
                  src="/images/design-mode/arrow.png"
                  alt="Arrow"
                  width={60}
                  height={18}
                  className="w-auto h-[28px]"
                />
              </h1>
            ) : (
              <button 
                onMouseDown={handlePressStart}
                onMouseUp={handlePressEnd}
                onMouseLeave={() => {
                  if (pressTimer.current) {
                    clearTimeout(pressTimer.current);
                    pressTimer.current = null;
                  }
                }}
                onTouchStart={handlePressStart}
                onTouchEnd={handlePressEnd}
                onContextMenu={handleContextMenu}
                className="hover:opacity-80 transition-opacity cursor-pointer select-none"
                aria-label="Przejdź do strony głównej"
              >
                <Image
                  src="/images/design-mode/logo.png"
                  alt="Dyslexa"
                  width={90}
                  height={24}
                  className="h-[24px] w-auto pointer-events-none"
                  draggable={false}
                />
              </button>
            )}

            {/*
              * The right column, which the note above describes as the matched
              * pair of the left one. It held the bug-report button until that was
              * removed, and stood empty between then and now; Help fills it, so
              * the logo goes on being centred on the row rather than between
              * whatever the sides happen to hold.
              */}
            <div className="flex justify-end">
              <HelpButton onClick={() => setHelpOpen(true)} />
            </div>
          </div>
        </div>
      </div>
      <BugReportModal
        isOpen={bugModalOpen}
        onClose={() => setBugModalOpen(false)}
        getLogs={() => logger.exportLogs()}
        context={bugReportContext}
      />
      <HelpOverlay open={helpOpen} onOpenChange={setHelpOpen} />
    </header>
  )
}
